import { useNavigate, useParams } from "react-router-dom";
import { FieldErrors, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { confirmDialog } from "primereact/confirmdialog";

import { TransactionWaiverFormRule, TransactionWaiverFormSchema } from "@factory/form/form.rule";
import { useFormDefaultTransactionWaiver } from "@factory/form/form-default-transaction-waiver.hook";
import { useFormApiMapper } from "@factory/services/form-api-mapper.hook";
import { ActionEnum } from "@factory/model/actions.enum";
import { getFormErrorMessage } from "@core/utility/get-error-message";
import { 
  useQyUpdateTransactionWaiver, 
  useQyGetTransactionWaiverById, 
  useQyDeleteTransactionWaiver 
} from "@factory/query/transaction-waiver.query";
import { GetResponseTransactionWaiverModel } from "@factory/model/query-model-transaction-waiver";
import { useNotificationContext } from "@shared/ui/notification/notification.context";
import { ButtonAction } from "@shared/ui/header-button/button-action";
import { useDateTimeContext } from "@shared/utility/date-time-service/date-time.context";

export function useEditTransactionWaiver() {
  // LOCAL DECLARATION
  const { formatDate } = useDateTimeContext();
  const { getDefault } = useFormDefaultTransactionWaiver();
  const navigate = useNavigate();
  const { mapUpdateTransactionWaiver, mapDeleteTransactionWaiver } = useFormApiMapper();
  const { showError, showSuccess } = useNotificationContext();
  const { transactionWaiverId } = useParams();
  const actions: ButtonAction[] = [
    { label: "Update", type: "split", value: ActionEnum.Update },
    { label: "Delete", type: "more", value: ActionEnum.Delete },
  ];

  // QUERY DECLARATION
  // API GETBYID TransactionWaiver
  const handleGetApiSuccess = (data: GetResponseTransactionWaiverModel) => {
    if (!data || data.data?.length === 0) {
      return;
    }

    const responseData = data.data?.[0];

      setValue("batch", responseData?.batch || "");
      setValue("waiver", responseData?.waiver || "");
      setValue("signature", responseData?.signature || "");
  };
  const {
    data: transactionWaiverResponse,
    isLoading,
    isError: transactionWaiverError,
  } = useQyGetTransactionWaiverById(transactionWaiverId || "", handleGetApiSuccess);
  const transactionWaiverData = transactionWaiverResponse?.data?.[0];

  // API UPDATE TransactionWaiver
  const handleUpdateApiSuccess = () => {
    showSuccess("TransactionWaiver updated successfully");
  };
  const { mutate: updateTransactionWaiver, isLoading: isEditLoading } = useQyUpdateTransactionWaiver(
    handleUpdateApiSuccess
  );

  // API DELETE TransactionWaiver
  const handleDeleteApiSuccess = () => {
    handleBack();
    showSuccess("TransactionWaiver deleted successfully");
  };
  const { mutate: deleteTransactionWaiver } = useQyDeleteTransactionWaiver(
    handleDeleteApiSuccess
  );

  // LOCAL FUNCTIONS
  const formMethod = useForm<TransactionWaiverFormSchema>({
    defaultValues: getDefault(transactionWaiverData),
    resolver: zodResolver(TransactionWaiverFormRule),
  });
  const { handleSubmit, setValue, watch, getValues } = formMethod;
  const handleValidate = (form: TransactionWaiverFormSchema) => {
    if (!transactionWaiverData || !transactionWaiverData.code) {
      throw new Error("No transactionWaiver data");
    }

    const formData = mapUpdateTransactionWaiver(form, transactionWaiverData.code);
    updateTransactionWaiver(formData);
  };
  const handleValidateError = (err: FieldErrors<TransactionWaiverFormSchema>) => {
    const formMessage = getFormErrorMessage(err);
    showError(formMessage);
  };
  const handleBack = () => {
    navigate("../");
  };
  const accept = () => {
    if (!transactionWaiverData || !transactionWaiverData.code) {
      throw new Error("No transactionWaiver data found");
    }

    const form = mapDeleteTransactionWaiver(transactionWaiverData.code);
    deleteTransactionWaiver(form);
  };
  const handleAction = (action: ButtonAction) => {
    switch (action.value) {
      case ActionEnum.Update:
        handleSubmit(handleValidate, handleValidateError)();
        break;
      
      case ActionEnum.Delete:
        confirmDialog({
          message: "Are you sure you want to delete this record?",
          header: "Delete Confirmation",
          icon: "pi pi-info-circle",
          defaultFocus: "reject",
          acceptClassName: "p-button-danger",
          accept,
        });
        break;
    }
  };

   return {
    transactionWaiverId,
    transactionWaiverData,
    isLoading,
    transactionWaiverError,
    formMethod,
    actions,
    isEditLoading,
    handleSubmit,
    setValue,
    watch,
    getValues,
    handleBack,
    handleAction,
  };
}